import { logisticsKeys } from '@d-orca/shared/client/hooks';
import { useQuery } from '@tanstack/react-query';
import { fetchDeliveryStatuses, fetchSiteLogistics, fetchSuppliers, logisticsQueryRetry } from '@apis/logistics';

/**
 * The page's server-state reads: the logistics payload (KPIs,
 * deliveries, inventory, zones, waste, …), the board's own column set and the
 * site's supplier list.
 *
 * Three separate queries, not one — the column set and the suppliers each
 * have their own cache entry so a rename, a reorder or a supplier edit
 * doesn't force a refetch of every delivery, stock level and KPI on the page
 * (see `statusesQuery`'s own invalidations in `useLogisticsMutations`).
 */
export function useLogisticsQueries(siteId: string) {
  const logisticsQuery = useQuery({
    queryKey: ['logistics', siteId],
    queryFn: ({ signal }) => fetchSiteLogistics(siteId, signal),
    retry: logisticsQueryRetry,
    // Always this page's own fetch on mount: the page sends the revision it
    // reads here, and a cache the dashboard filled earlier may be old.
    refetchOnMount: 'always',
  });
  /* The board's columns. Its own cache entry, not part of the logistics
     payload: a rename or a reorder must not force a refetch of every delivery,
     stock level and KPI on the page. */
  const statusesQuery = useQuery({
    queryKey: ['logistics-statuses', siteId],
    queryFn: ({ signal }) => fetchDeliveryStatuses(siteId, signal),
    retry: logisticsQueryRetry,
  });

  /* The site's supplier list: its own cache entry, so adding one never
     refetches the payload. Reloaded with the page by the refresh-to-edit hook. */
  const suppliersQuery = useQuery({
    queryKey: logisticsKeys.suppliers(siteId),
    queryFn: ({ signal }) => fetchSuppliers(siteId, signal),
    retry: logisticsQueryRetry,
  });

  return { logisticsQuery, statusesQuery, suppliersQuery };
}
